// Surface themes — DESIGN_LANGUAGE.md §2.2. Brand owns every surface theme
// now: `card` (branded 2026-09-23, 3.14.792), and since 3.14.798 (lane D1 of
// the white-label redesign) the overlay sheet `dialog` (white / gray3) and
// the two veils `backdrop` (scrim + blur(8px), behind every modal, side
// panel and phone sheet) and `backdropSoft` (PageSlot's non-blocking veil).
// The copies that lived here were DEAD the moment brand defined the same
// leaves (`deepDefaults` below only fills a MISSING leaf) and are gone;
// __tests__/stagedOverlayTokens.test.js locks brand's values to the
// contract and keeps this file from re-forking them.
export default {
  // THE SEGMENTED TRACK (AppSegmented, 2026-09-30; Nika via "unreal crm": "the
  // CRM submenu buttons lose their background on other Windows devices" —
  // Windows follows the LIGHT scheme by default). The old track, brand `layer`
  // (gray2.04 in light), read 1.08:1 against the sheet, the header and the page,
  // and the white view thumb on it 1.08-1.22:1 — "no background".
  //   light: the brand's NEUTRAL PLATE `widgetPlate` ('gray2.07' — its light
  //          half; the brand names it for "a segmented track") — 1.15:1 on
  //          every light ground, the white thumb 1.15-1.30:1 on it (with the
  //          `float` ring and shadow);
  //   dark:  `layer`'s own dark value, unchanged ('gray7.1'). widgetPlate's
  //          dark half (white.13) over the dark sheet (gray3) equals the dark
  //          thumb (`raised` gray5): 1.08:1 — the chosen view would vanish in
  //          every dark dialog; on this value it reads 1.49:1 there and 1.78:1
  //          on the page.
  // __tests__/segmentedTrackContrast.test.js pins both halves to the brand
  // tokens and the ratios to their floors.
  segmentTrack: {
    '@dark': { background: 'gray7.1' },
    '@light': { background: 'gray2.07' }
  },
  // THE SEARCH GLASS (Nika 2026-09-30, verbatim: "make searchdropdon bg blur") — the search dropdown (the
  // launcher, idle and with typed results) as a VISIBLE glass. `canvas-card-glass` paints gray1 at 85 % in dark
  // (white 95 % in light), so its 35 px backdrop blur barely read. This variant takes the brand's card-glass
  // alpha — the two halves of the `cardGlass` token (brand color.js: ['white.62', 'gray1.55']) — and the house
  // blur by reference (one source of truth). A NEW name, on purpose: the global `canvas-card-glass` every other
  // surface wears stays as it is (a brand-owned name could not be overridden from here anyway — see this file's notes).
  // AA: every launcher ink keeps >= 4.5:1 over this fill on each theme ground and surface
  // (workspace __tests__/searchGlass.test.js).
  'search-glass': {
    '@dark': {
      background: 'gray1.55',
      color: 'gray12',
      backdropFilter: 'var(--theme-canvas-card-glass-backdropFilter)'
    },
    '@light': {
      background: 'white.62',
      color: 'gray2',
      backdropFilter: 'var(--theme-canvas-card-glass-backdropFilter)'
    }
  }
  // ⚠ DO NOT add a `common-box`/`common-box-secondary` override HERE to fix
  // brand's translucent `white.75`/`white.65` @light fills (tickets/opus.md
  // item 6) — it is DEAD CODE, proven live (2026-08-14) and reproduced
  // against the real merge function
  // (`smbls/packages/utils/sharedLibraries.js`'s `mergeSharedLibraries`/
  // `mergeOneLibrary`/`deepDefaults`), not assumed:
  //
  // The `card`/`dialog` copies that once lived here worked ONLY while brand
  // did not define those keys — `deepDefaults` copies a MISSING key in
  // wholesale, unopposed (both are brand-owned now, see the header).
  // `common-box`/`common-box-secondary` are NOT missing: brand
  // (`company/packages/brand/designSystem/theme.js:586-601`) already
  // defines them, and every one of this monorepo's apps
  // (workspace/canvas/marketplace/preview/design-system/workspace-ui, all
  // checked) lists `brand` BEFORE `shared` in its OWN `sharedLibraries.js`.
  // `mergeSharedLibraries` runs brand first — by the time `shared` (this
  // file) is merged, `context.designSystem.theme['common-box']['@light'].
  // background` already has brand's `white.75` from library #1, and
  // `deepDefaults` ONLY fills a leaf that is MISSING, never overwrites one
  // that already exists — so this file's value is silently discarded no
  // matter what it says. The "D5 pattern already proven for card/dialog"
  // is not a precedent for OVERRIDING an existing brand value at any
  // depth — it never demonstrated that, and cannot.
  //
  // The only place a `context.designSystem`-level override reliably WINS
  // (`mergeOneLibrary`'s target-always-wins-on-conflict deepDefaults
  // semantics) is each APP's OWN native design-system source, BEFORE any
  // library merge runs — e.g. `workspace/packages/workspace/designSystem/
  // theme.js` for the shell (see `functions/extensionDesignSystem.js`'s
  // own comment: "brand fills gaps under this exact path. Target wins on
  // conflicts."). But `common-box` is consumed by ~35 sites across EVERY
  // app in this monorepo plus company/brand/landing/no-code-landing
  // directly — duplicating this override into every app's own native
  // theme.js is the exact "per-component hack" item 6 explicitly rejects,
  // one layer up (per-APP instead of per-component), and a new app would
  // silently reintroduce the bug until someone remembers to copy it again.
  //
  // The correct, complete fix is brand-tier
  // (`company/packages/brand/designSystem/theme.js`) — proposal already
  // filed on `tickets/fable.md` (brand/ is a shared library: read freely,
  // ask before editing). Item 6 stays OPEN, blocked on that permission,
  // rather than an app-local override that would read as "fixed" while
  // leaving every other consumer exactly as broken.
}
